import { useTranslation } from "@/hooks/useTranslation";
import { Label } from "@/components/ui/label";
import type { Event } from "@/services/api/events";
import { eventPlanOf, isUpgradable } from "@/utils/purchaseEvent";

interface PurchaseEventPickerProps {
  events: Event[];
  selectedUuid: string | null;
  onSelect: (uuid: string) => void;
}

/**
 * JUB-157: choose which event a plan is bought for. Events already on
 * Premium are listed (so the buyer sees them) but cannot be chosen.
 */
export function PurchaseEventPicker({ events, selectedUuid, onSelect }: PurchaseEventPickerProps) {
  const { t } = useTranslation();

  return (
    <div className="mx-auto mt-4 max-w-md text-left" data-testid="purchase-event-picker">
      <Label htmlFor="purchase-event-select" className="text-sm font-medium">
        {t("landing.pricing.chooseEvent")}
      </Label>
      <select
        id="purchase-event-select"
        className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-500"
        value={selectedUuid ?? ""}
        onChange={(e) => e.target.value && onSelect(e.target.value)}
        data-testid="purchase-event-select"
      >
        <option value="" disabled>
          {t("landing.pricing.chooseEventPlaceholder")}
        </option>
        {events.map((event) => {
          const plan = eventPlanOf(event);
          return (
            <option key={event.uuid} value={event.uuid} disabled={!isUpgradable(event)}>
              {event.name} · {t(`landing.pricing.${plan}.name`) || plan}
            </option>
          );
        })}
      </select>
    </div>
  );
}
